import { useCallback, useEffect, useRef } from "react";

// region External libraries
import { useQuery } from "@apollo/client";
import { useMsal } from "@azure/msal-react";
// endregion External libraries
// region Hooks
import { useCommandMutation } from "@hooks/useCommandMutation";
// endregion Hooks
// region Store
import {
  MyAnnouncement,
  myAnnouncementsQuery,
  TMyAnnouncementsQuery
} from "src/store/graphql/groups/announcement/query.graphql";
import {
  dismissAnnouncementMutation,
  markAllAnnouncementsReadMutation,
  markAnnouncementReadMutation,
  markAnnouncementsReadMutation,
  registerAnnouncementClickMutation,
  TDismissAnnouncementMutation,
  TMarkAllAnnouncementsReadMutation,
  TMarkAnnouncementReadMutation,
  TMarkAnnouncementsReadMutation,
  TRegisterAnnouncementClickMutation
} from "src/store/graphql/groups/announcement/mutation.graphql";
// endregion Store

// Same cadence as the permissions reload in pages/Office.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

// Server-side cap of GetMyAnnouncements; one page feeds bell, banner and "view all".
const PAGE_SIZE = 50;

// Rows of the bell dropdown come into view together, so exposures are collected
// for a moment and sent as one mutation instead of one request per row.
const EXPOSURE_FLUSH_MS = 500;

const pickMarkRead = (data: TMarkAnnouncementReadMutation) => data.announcement.markAnnouncementRead;
const pickMarkManyRead = (data: TMarkAnnouncementsReadMutation) => data.announcement.markAnnouncementsRead;
const pickMarkAllRead = (data: TMarkAllAnnouncementsReadMutation) => data.announcement.markAllAnnouncementsRead;
const pickRegisterClick = (data: TRegisterAnnouncementClickMutation) => data.announcement.registerAnnouncementClick;
const pickDismiss = (data: TDismissAnnouncementMutation) => data.announcement.dismissAnnouncement;

export interface MyAnnouncementsState {
  announcements: MyAnnouncement[];
  unreadCount: number;
  loading: boolean;
  markRead: (id: string) => Promise<boolean>;
  markAllRead: () => Promise<boolean>;
  /** Reports that the announcement was on the user's screen; queued and sent in batch. */
  markReadOnExposure: (id: string) => void;
  registerClick: (id: string) => Promise<boolean>;
  dismiss: (id: string) => Promise<boolean>;
}

export interface MyAnnouncementsOptions {
  /**
   * When false the consumer piggybacks on the shared Apollo cache without
   * scheduling its own poll (e.g. the Launcher banner, while the navbar bell
   * already polls the same query).
   */
  poll?: boolean;
}

/**
 * The current user's active announcements (dismissed ones are filtered by the
 * backend) plus the interaction mutations. Every mutation refetches the list
 * silently so all consumers of the shared cache stay in sync.
 */
export const useMyAnnouncements = (options?: MyAnnouncementsOptions): MyAnnouncementsState => {

  const { instance } = useMsal();
  const isAuthenticated = Boolean(instance.getActiveAccount());

  const { data, loading, refetch } = useQuery<TMyAnnouncementsQuery>(myAnnouncementsQuery, {
    variables: { page: 1, perPage: PAGE_SIZE },
    skip: !isAuthenticated,
    pollInterval: options?.poll === false ? 0 : POLL_INTERVAL_MS,
    // Background tabs do not need fresh notifications; they catch up on focus.
    skipPollAttempt: () => document.hidden
  });

  const refresh = useCallback(() => refetch(), [refetch]);
  const silentRefresh = { silent: true, onSuccess: refresh };

  const { run: runMarkRead } = useCommandMutation(markAnnouncementReadMutation, pickMarkRead, silentRefresh);
  const { run: runMarkManyRead } = useCommandMutation(markAnnouncementsReadMutation, pickMarkManyRead, silentRefresh);
  const { run: runMarkAllRead } = useCommandMutation(markAllAnnouncementsReadMutation, pickMarkAllRead, silentRefresh);
  const { run: runRegisterClick } = useCommandMutation(registerAnnouncementClickMutation, pickRegisterClick, silentRefresh);
  const { run: runDismiss } = useCommandMutation(dismissAnnouncementMutation, pickDismiss, silentRefresh);

  const pendingRef = useRef<string[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markReadOnExposure = useCallback((id: string) => {
    if (pendingRef.current.includes(id)) return;

    pendingRef.current.push(id);

    if (flushTimerRef.current) return;

    flushTimerRef.current = setTimeout(() => {
      const ids = pendingRef.current;

      flushTimerRef.current = null;
      pendingRef.current = [];

      runMarkManyRead({ ids });
    }, EXPOSURE_FLUSH_MS);
  }, [runMarkManyRead]);

  useEffect(() => () => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
  }, []);

  const markRead = useCallback((id: string) => runMarkRead({ id }), [runMarkRead]);
  const markAllRead = useCallback(() => runMarkAllRead(), [runMarkAllRead]);
  const registerClick = useCallback((id: string) => runRegisterClick({ id }), [runRegisterClick]);
  const dismiss = useCallback((id: string) => runDismiss({ id }), [runDismiss]);

  const announcements = data?.announcement?.getMyAnnouncements?.records ?? [];
  const unreadCount = announcements.filter((item) => !item.readAt).length;

  return {
    announcements,
    unreadCount,
    loading,
    markRead,
    markAllRead,
    markReadOnExposure,
    registerClick,
    dismiss
  };
};
